// The ready-list physician home (behind the `readyList` flag; see DashboardPage). Today on the left, Needs you on the
// right. One row of Today is open at a time. Data: use-ready-list-data.ts.
// No "Your day" strip while `yourDayOff` is ON (PROD-3382; Antonin and Samer, 3 Nov call: "it's not bringing anything, we
// should kill it"); `?x=-your-day-off` draws it across the top again.
import { useState } from 'react';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { useNavigate } from 'react-router-dom';
import { toEncounterNav } from '@/encounter/lib/to-patient-header';
import { NeedsYouCard } from './needs-you-card';
import { NeedsYouModal } from './needs-you-modal';
import { useSeenNeeds } from './use-seen-needs';
import { TodayCard, patientIdOf } from './today-card';
import { YourDayCard } from './your-day-card';
import { useReadyListData } from './use-ready-list-data';
import { TeamHomeCard } from '@/staff-chat/components/team-home-card';
import { countByGlyph, type NeedItem, type ReadyRow } from './ready-list-data';
import { useAuthStore } from '@/auth/auth-store';
import { waitingOrNext, useIntelWarmTarget } from '@/encounter/intelligence/use-intel-warm';

/** The page header's count line (prototype home: "5 visits today · 1 in room · 2 waiting"): today's visits and the two
 *  stages that are happening now. While the list loads, and on a day with no visits (Today already says so), the date
 *  stands in. Leaves out
 *  the prototype's "waiting longest, since 08:05": no check-in time on the wire. */
export function ReadyListCountLine({ fallback }: { fallback: string }) {
  const d = useReadyListData();
  const n = d.rows.length;
  if (d.appointments.loading || n === 0) return <>{fallback}</>;
  const c = countByGlyph(d.rows);
  const parts = [`${n} ${n === 1 ? 'visit' : 'visits'} today`];
  if (c.full > 0) parts.push(`${c.full} in session`);
  if (c.half > 0) parts.push(`${c.half} checked in`);
  return <>{parts.join(' · ')}</>;
}

export function ReadyListHome() {
  const navigate = useNavigate();
  const d = useReadyListData();
  const [yourDayOff] = useState(() => isEncounterFlagOn('yourDayOff'));
  // One row open: the next visit to be seen until the user picks another (or closes it).
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const expandedId = picked === undefined ? (d.nextId ?? null) : picked;
  // Warm-from-Today (4 Nov): the next visit to be seen (the row opened by default) has its Explain warmed once, with this
  // clinician's token; nothing is shown. Only once the list and the drafts have loaded, and only the clinician's own visit.
  const user = useAuthStore((s) => s.user);
  useIntelWarmTarget(d.appointments.loading ? null : waitingOrNext(d.rows, d.nextId, user?.practitionerId ?? user?.id));

  const openEncounter = (r: ReadyRow) => {
    const state = toEncounterNav(r.item);
    void navigate(`/encounter/${r.id}`, { state });
  };
  // A Needs you item opens in a modal (and goes quiet); its action opens the encounter. A draft from today's list opens
  // with the visit's nav state; an older one is a plain deep link.
  const { seen, markSeen } = useSeenNeeds();
  const [openNeedId, setOpenNeedId] = useState<string | null>(null);
  const openNeed = (n: NeedItem) => {
    markSeen(n.id);
    setOpenNeedId(n.id);
  };
  const openNeedEncounter = (n: NeedItem) => {
    const row = d.rows.find((r) => r.id === n.id);
    setOpenNeedId(null);
    void navigate(`/encounter/${n.id}`, row ? { state: toEncounterNav(row.item) } : undefined);
  };

  const select = (id: string) => {
    setPicked(id);
    requestAnimationFrame(() => document.querySelector(`[data-schedule-row="${id}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  };

  return (
    <div data-ready-list data-enc-look className="ready-list">
      {yourDayOff ? null : <YourDayCard rows={d.rows} nowMs={d.nowMs} selectedId={expandedId} onSelect={select} />}
      <div className="ready-grid">
        <TodayCard
          rows={d.rows}
          expandedId={expandedId}
          onToggle={(id) => setPicked(expandedId === id ? null : id)}
          onOpenEncounter={openEncounter}
          onOpenPatient={(r) => {
            const id = patientIdOf(r);
            if (id) void navigate(`/patients/edit/${id}`);
          }}
          onOpenCalendar={() => void navigate('/calendar')}
          loading={d.appointments.loading}
          error={d.appointments.error}
          onRetry={d.appointments.retry}
        />
        <div className="ready-side">
          <NeedsYouCard items={d.needs.items} seen={seen} loading={d.needs.loading} error={d.needs.error} onRetry={d.needs.retry} onOpen={openNeed} />
          {/* Staff chat (SR-14, `?x=staff-chat`): the Team card, the latest conversations; opens the dock. Nothing when off. */}
          <TeamHomeCard />
        </div>
      </div>
      <NeedsYouModal item={d.needs.items.find((n) => n.id === openNeedId) ?? null} onClose={() => setOpenNeedId(null)} onOpenEncounter={openNeedEncounter} />
    </div>
  );
}
